/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles';
import { Box, CircularProgress, Dialog, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ArrowDropUp, CancelPresentationRounded, ChevronLeft, ChevronRight, Circle, Close, LinearScaleOutlined, PreviewRounded, PrintRounded, Square } from '@mui/icons-material';
import CanvasJSReact from '../../canvasjs/canvasjs.react';
import Datagrid from '../../Datagrid';
import { useDispatch, useSelector } from 'react-redux';
import { HYDRAULICS_CHART_COLORS, STRIPPING_TRIP_TYPE_COLORS } from '../../../../shared/config/hydraulics_constants';
import { getOptimalInterval } from '../../../../utils/getOptimalInterval';
import { CHART_MARKERTYPES, STRIPPING_TYPE_PRIMARY, STRIPPING_TYPE_SECONDARY } from '../../../../utils/constants';
import { downloadStrippingPDF, listStrippingResults } from '../../../../store/actions/stripping';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const StrippingChart = ({
  onCloseClick,
  isOpen,
  rowData,
  wellDetails
}) => {
  const classes = styles();
  const [tableRow, setTableRows] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const { strippingResults, downloadReportPdf } = useSelector((state) => state.stripping);
  const dispatch = useDispatch();
  const [plotForPrimary, setPlotForPrimary] = useState('ECD');
  const [plotForSecondary, setPlotForSecondary] = useState('Hole Depth');
  const window = useSelector((state) => state.wellsWindow);
  const [chartData, setChartData] = useState([]);
  const containerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const [tableColumn, setTableColumn] = useState([]);
  const [minMax_X, setMinMax_X] = useState({});
  const [chartLegendData, setChartLegendData] = useState([]);
  const [hiddenChartData, setHiddenChartData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [secondaryDepthField, setSecondaryDepthField] = useState([]);

  useEffect(() => {
    if (loader) {
      document.body.style.cursor = 'wait';
    } else {
      document.body.style.cursor = 'default';
    }
    return () => {
      document.body.style.cursor = 'default';
    };
  }, [loader]);

  const handleScroll = () => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
  };

  const [chartConfig, setChartConfig] = useState([
    {
      name: 'PP',
      key: 'pp',
      color: HYDRAULICS_CHART_COLORS.PORE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> PP: {x} ${window?.units?.pp?.label}`
    },
    {
      name: 'FP',
      key: 'fp',
      color: HYDRAULICS_CHART_COLORS.FRACTURE_PRESSURE,
      hide: false,
      tooltip: `MD: {y} ${window?.units?.md?.label} <br/> FP: {x} ${window?.units?.fp?.label}`
    },
  ]);

  useEffect(() => {
    const body = {
      runId: rowData?.row?._id,
      wellsInfoId: rowData?.row?.wellsInfoId
    };
    dispatch(listStrippingResults(body));
  }, []);

  useEffect(() => {
    const shouldShow = primaryField.field === 'ecd_with_sbp' || primaryField.field === 'esd_with_sbp';
    setChartConfig(prevItems =>
      prevItems.map(item => ({
        ...item,
        hide: !shouldShow
      }))
    );
  }, [plotForPrimary]);

  const primaryField = STRIPPING_TYPE_PRIMARY.find(
    (type) => type.label === plotForPrimary
  );

  const secondaryField = secondaryDepthField.find(
    (type) => type.label === plotForSecondary
  );

  useEffect(() => {
    const secondaryFields = strippingResults?.data?.[0]?.secondary_fields;
    setSecondaryDepthField(secondaryFields || STRIPPING_TYPE_SECONDARY);
  }, [strippingResults?.data]);

  useEffect(() => {
    if (!strippingResults?.data?.length) return;
    const units = strippingResults.units?.depth_points;
    const depth_summary_units = strippingResults.units?.depths_summary;

    const typeCountMap = {};
    const graphData = strippingResults?.data?.map(({ results = [], inputs = {} }, index) => {
      const tripType = inputs.trip.type;
      if (!typeCountMap[tripType]) {
        typeCountMap[tripType] = 0;
      }
      const markerIndex = typeCountMap[tripType] % CHART_MARKERTYPES.length;
      const markerType = CHART_MARKERTYPES[markerIndex];

      typeCountMap[tripType] += 1;
      return {
        inputs,
        index,
        runNumber: index + 1,
        markerType,
        hide: false,
        color: STRIPPING_TRIP_TYPE_COLORS[tripType],
        type: 'line',
        toolTipContent: `Run-${index + 1}: ${inputs.trip.speed} ${strippingResults?.units?.inputs?.speed?.label} <br/> Bit Depth: {y} ${units?.bit_depth?.label} <br/> ${(primaryField.label) + ' @ ' + (secondaryField?.label)}: {x} ${depth_summary_units?.[secondaryField?.field]?.[primaryField?.field]?.label} <br/>`,
        depth_points: results.map((item) => ({
          ...item.depths_summary?.hole_depth

        })),
        dataPoints: results.map((item) => ({
          x: item?.depths_summary?.[secondaryField?.field]?.[primaryField.field],
          y: item.bit_depth,
        })),
      };
    });
    const extractDataPints = graphData.map((item) => {
      return { dataPoints: item.dataPoints, };
    });
    const dataPointXs = extractDataPints.flatMap(v => v.dataPoints.map(p => p.x));
    let ppfpXValues = [];

    if (primaryField.field === 'ecd_with_sbp' || primaryField.field === 'esd_with_sbp') {
      ppfpXValues = window?.data?.flatMap(e => [e.pp, e.fp]) || [];
    }

    const allXs = [...dataPointXs, ...ppfpXValues];
    const minX = Math.min(...allXs);
    const maxX = Math.max(...allXs);

    setMinMax_X({
      minX,
      maxX
    });
    const depthSet = new Set();
    extractDataPints.forEach(run => run.dataPoints.forEach(({ y }) => depthSet.add(y)));
    const sortedDepths = [...depthSet].sort((a, b) => a - b);

    const mapDataPoints = extractDataPints.map(run => {
      const map = new Map();
      run.dataPoints.forEach(({ x, y }) => map.set(y, x));
      return map;
    });

    const taleData = sortedDepths.map(bitDepth => {
      const entry = {
        bitDepth,
        _id: Math.random().toString(36).substring(2, 10)
      };
      mapDataPoints.forEach((map, idx) => {
        entry[`run${idx + 1}`] = map.get(bitDepth) ?? null;
      });
      return entry;
    });
    const columns_table = [
      {
        field: 'bitDepth',
        headerName: 'Bit Depth',
        renderHeader: () => (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
              Bit Depth ({strippingResults?.units?.depth_points?.bit_depth?.label})
              </Typography>
            </Box>
        ),
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const value = Number(params.value);
          return isNaN(value) ? params.value : value.toFixed(strippingResults?.units?.depth_points?.bit_depth?.decimals);
        },
        width: 100,
        headerClassName: 'table-header',
        align: 'center',
        headerAlign: 'center',
      },
      ...graphData.map((col, index) => ({
        field: `run${index + 1}`,
        headerName: `Run ${index + 1}: ${col?.inputs?.trip?.speed || ''} ${strippingResults?.units?.inputs?.speed?.label} ${primaryField.label} (${strippingResults?.units?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label}) @ ${(secondaryField?.label)}  `,
        width: 158,
        renderHeader: () => (
          <Box>
            <Typography className={classes.headerStyle}>
              {`Run ${index + 1}: ${col?.inputs?.trip?.speed || ''} ${strippingResults?.units?.inputs?.speed?.label}`}
            </Typography>
            <Typography className={classes.headerStyle}>
              {`${primaryField.label} (${strippingResults?.units?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label})`}
            </Typography>
            <Typography className={classes.headerStyle}>
            {`@ ${(secondaryField?.label)}`}
            </Typography>
          </Box>
        ),
        headerAlign: 'center',
        align: 'center',
        headerClassName: 'table-header',
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') {
            return '';
          }
          const value = Number(params.value);
          return isNaN(value) ? params.value : value.toFixed(strippingResults?.units?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.decimals);
        },
      })),
    ];
    setTableColumn(columns_table);
    setTableRows(taleData);
    setChartData(graphData);
    setChartLegendData(graphData);
    setHiddenChartData(graphData);
  }, [strippingResults, plotForPrimary, plotForSecondary]);

  useEffect(() => {
    setShowTable(!strippingResults.loading && !!strippingResults.data);
  }, [strippingResults.loading, strippingResults.data]);

  useEffect(() => {
    const filteredData = hiddenChartData.filter(item => {
      const row = chartLegendData.find(row => row.index === item.index);
      return !row?.hide;
    });
    setChartData(filteredData);
  }, [chartLegendData]);

  const generateWindowDataPoints = (config) => {
    const extractAllDepthPointsArrays = (data) => {
      return data.flatMap(item => item.depth_points);
    };
    const allDepthPoints = extractAllDepthPointsArrays(chartData);
    const dataPoints = [];

    if (window?.data.length === 1) {
      const singleDataPoint = window?.data[0];
      dataPoints.push(
        { x: singleDataPoint[config.key], y: 0, markerType: 'none' },
        { x: singleDataPoint[config.key], y: singleDataPoint.md },
        { x: singleDataPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' }
      );
    } else if (window?.data.length > 1) {
      const firstPoint = window?.data[0];
      const lastPoint = window?.data.at(-1);

      if (firstPoint.md > 0) {
        dataPoints.push({ x: firstPoint[config.key], y: 0, markerType: 'none' });
      }

      window?.data.forEach(item => {
        dataPoints.push({ x: item[config.key], y: item.md });
      });

      dataPoints.push({ x: lastPoint[config.key], y: allDepthPoints.at(-1).md, markerType: 'none' });
    }

    const formattedData = dataPoints.map(item => {
      const formattedItem = {
        x: parseFloat(Number(item.x).toFixed(window?.units?.md?.decimals)),
        y: parseFloat(Number(item.y).toFixed(window?.units?.pp?.decimals)),
      };
      if (item.markerType) {
        formattedItem.markerType = item.markerType;
      }
      return formattedItem;
    });
    return formattedData;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, [chartData]);

  const CustomLegend = () => {
    const updateHide = (key, hideValue) => {
      handleScroll();
      setChartConfig(prevItems =>
        prevItems.map(item =>
          item.key === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    const updateHideStrippingLegend = (key, hideValue) => {
      handleScroll();
      setChartLegendData(prevItems =>
        prevItems.map(item =>
          item.index === key ? { ...item, hide: hideValue } : item
        )
      );
    };

    return strippingResults.loading
      ? null
      : (
      <Grid container ref={containerRef} onScroll={handleScroll} height={'30px'}>
        {(primaryField.field === 'esd_with_sbp' || primaryField.field === 'ecd_with_sbp') &&
          chartConfig.map(item => (
            <Box
              key={item.name}
              className={classes.legendItem}
              sx={{ opacity: item.hide ? 0.4 : 1 }}
              onClick={() => updateHide(item.key, !item.hide)}
            >
              {item.name}
              <LinearScaleOutlined fontSize="large" sx={{ color: item.color, mr: 1 }} />
            </Box>
          ))}
        {chartLegendData.map((item, index) => (
          <Box
            key={item.name + '' + index}
            className={classes.legendItem}
            sx={{ opacity: item.hide ? 0.4 : 1 }}
            onClick={() => updateHideStrippingLegend(item.index, !item.hide)}
          >
            {`Run ${index + 1}`}
            <IconButton
              sx={{
                '&::after': {
                  backgroundColor: item.color
                }
              }}
              className={classes.filterTableMarker}
            >
              {item?.markerType === 'triangle' && <ArrowDropUp sx={{ fontSize: 50, color: item.color }} />}
              {item?.markerType === 'circle' && <Circle sx={{ fontSize: 15, color: item.color }} />}
              {item?.markerType === 'square' && <Square sx={{ fontSize: 15, color: item.color }} />}
              {item?.markerType === 'cross' && <Close sx={{ fontSize: 25, color: item.color }} />}
            </IconButton>
          </Box>
        ))}
      </Grid>
        );
  };

  const options = useMemo(() => ({
    theme: 'light2',
    animationEnabled: true,
    zoomEnabled: true,
    exportEnabled: true,
    zoomType: 'y',
    toolbar: {
      itemBackgroundColor: '#d3d3d3',
      itemBackgroundColorOnHover: '#3e3e3e',
      buttonBorderColor: '#3e3e3e',
    },
    backgroundColor: '#FFF',
    axisX: {
      viewportMinimum: minMax_X.minX - 0.1,
      viewportMaximum: minMax_X.maxX + 0.1,
      interval: getOptimalInterval(minMax_X.minX, minMax_X.maxX, 10),
      valueFormatString: '0.0',
      labelFontSize: 16,
      gridThickness: 1,
      title: `${primaryField.label + ' @ ' + (secondaryField?.label)} (${strippingResults?.units?.depths_summary?.[secondaryField?.field]?.[primaryField?.field]?.label})`,
      titleFontSize: 22,
    },
    axisY: {
      viewportMinimum: null,
      viewportMaximum: null,
      title: `Bit Depth (${strippingResults?.units?.depth_points?.bit_depth?.label})`,
      titleFontSize: 22,
      labelFontSize: 16,
      includeZero: false,
      valueFormatString: '0',
      reversed: true,
      labelPlacement: 'outside',
      gridThickness: 1,
    },
    height: 800,
    legend: {
      fontSize: 20,
    },
    data: [
      ...chartConfig.map((config) => {
        if (chartData.length > 0) {
          if (config.hide) return {};
          if (config.key === 'pp' || config.key === 'fp') {
            return {
              type: 'line',
              name: config.name,
              showInLegend: false,
              toolTipContent: config.tooltip,
              dataPoints: generateWindowDataPoints(config),
              color: config.color,
            };
          }
        }
        return {};
      }),
      ...chartData,
    ],
  }), [chartConfig, chartData]);

  return (
      <Dialog
        open={isOpen}
        onClose={() => {
          onCloseClick();
        }}
        maxWidth={'lg'}
        fullWidth={true}
        style={{
          marginRight: '20px'
        }}
        PaperProps={{
          sx: {
            overflow: 'visible'
          },
        }}
        className={classes.dialogModal}
      >
        <Box className={classes.dialogTitle}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                {`${wellDetails?.Rig} | ${wellDetails?.Well} | ${rowData?.row?.name}`}
              </Grid>
              <Grid item>
              <IconButton
                disabled={downloadReportPdf === 'loading' || strippingResults.loading}
                onClick={() => {
                  const body = {
                    jobId: rowData?.row?.jobId,
                    wellsInfoId: rowData?.row?.wellsInfoId,
                    runId: rowData?.row?._id,
                    wellName: wellDetails?.Well,
                    rigName: wellDetails?.Rig,
                    runName: rowData?.row?.name,
                    type: plotForPrimary,
                    depth: plotForSecondary
                  };
                  setLoader(true);
                  dispatch(downloadStrippingPDF(body, false, () => {
                    setLoader(false);
                  }));
                }}>
                <PrintRounded fontSize='large' />
              </IconButton>
              <IconButton
                disabled={downloadReportPdf === 'loading' || strippingResults.loading}
                onClick={() => {
                  const body = {
                    jobId: rowData?.row?.jobId,
                    wellsInfoId: rowData?.row?.wellsInfoId,
                    runId: rowData?.row?._id,
                    wellName: wellDetails?.Well,
                    rigName: wellDetails?.Rig,
                    runName: rowData?.row?.name,
                    type: plotForPrimary,
                    depth: plotForSecondary
                  };
                  setLoader(true);
                  dispatch(downloadStrippingPDF(body, true, () => {
                    setLoader(false);
                  }));
                }}>
                <PreviewRounded fontSize='large' />
              </IconButton>
                <IconButton
                  onClick={() => {
                    onCloseClick();
                  }}
                >
                  <CancelPresentationRounded fontSize='large' />
                </IconButton>
              </Grid>
            </Grid>
          </Typography>
        </Box>
        <Divider />
        <Box display="flex" justifyContent="space-around" marginLeft="2%" marginTop='1%' alignItems="center" width="400px">
          <Grid item>
            <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '160px' }}>
              <InputLabel id="filter-input-label">Type</InputLabel>
              <Select
                labelId="filter-select-id"
                id="filter-id"
                value={plotForPrimary}
                label="Filter"
                disabled={strippingResults?.loading || downloadReportPdf === 'loading'}
                onChange={(e) => setPlotForPrimary(e.target.value)}
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '18px',
                  background: 'unset'
                }}
              >
                {STRIPPING_TYPE_PRIMARY.map((item, idItem) => (
                  <MenuItem
                    key={`item_${item}_${idItem}`}
                    value={item.label}
                    sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    {'at'}
                </Typography>
          </Grid>
          <Grid item>
            <FormControl fullWidth sx={{ bgcolor: 'transparent', width: '200px' }}>
              <InputLabel id="filter-input-label">Depth</InputLabel>
              <Select
                labelId="filter-select-id"
                id="filter-id"
                value={plotForSecondary}
                label="Filter"
                disabled={strippingResults?.loading || downloadReportPdf === 'loading'}
                onChange={(e) => setPlotForSecondary(e.target.value)}
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '18px',
                  background: 'unset'
                }}
              >
                {secondaryDepthField.map((item, idItem) => (
                  <MenuItem
                    key={`item_${item}_${idItem}`}
                    value={item.label}
                    sx={{ color: item?.color, fontSize: '18px', fontWeight: 'bold' }}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Box>
        <Grid container item xs={showTable ? 8 : 12} marginTop={'1%'} alignItems="center" justifyContent="center">
          <Grid item display="flex">
            {<CustomLegend />}
          </Grid>
        </Grid>
        <Box style={{
          overflow: 'auto',
        }}>
          <Grid container>
            <Grid item xs={showTable ? 7 : 12} sx={{
              p: 2,
              pt: 4,
              '.canvasjs-chart-container canvas': {
                cursor: loader ? 'wait !important' : ''
              }
            }}>
              {strippingResults.loading
                ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ height: '700px' }}
                  >
                    <CircularProgress />
                  </Box>
                  )
                : (
                  <CanvasJSChart
                    options={options}
                  />

                  )}
            </Grid>
            {!showTable && <Box className={classes.menuTogglerBtn}>
              <IconButton
                onClick={() => {
                  setShowTable(!showTable);
                }}>
                <ChevronLeft sx={{ fontSize: '33px' }} />
              </IconButton>
            </Box>}
            {showTable && <Grid item xs={5} marginTop={'2.5%'}>
              <Box className={classes.menuTogglerTableBtn}>
                <IconButton
                  onClick={() => {
                    setShowTable(!showTable);
                  }}>
                  <ChevronRight sx={{ fontSize: '33px' }} />
                </IconButton>
              </Box>
              {showTable && <Grid item xs={12}>
                <Datagrid
                  fileName={`${wellDetails?.Well?.trim()}-${rowData?.row?.name?.trim()}-Stripping`}
                  loading={strippingResults.loading}
                  data={tableRow}
                  sx={{
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      padding: '2px',
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem',
                      fontWeight: '900 !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      overflow: 'visible',
                      lineHeight: '1.43rem',
                      whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      minHeight: '0px'
                    },
                    '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                      outline: 'none !important',
                    },
                    '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus':
                    {
                      outline: 'none !important',
                      height: '200px'
                    },
                  }}
                  columns={tableColumn}
                  disableSelectionOnClick
                  disableStickyHeader={true}
                  autoHeight={false}
                  hideFooterPagination
                  hideFooterSelectedRowCount
                  height={'800px'}
                  getRowHeight={() => 'auto'}
                  pageSize={100}
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: {
                        enabled: true,
                      }
                    },
                  }}
                />
              </Grid>}
            </Grid>}
          </Grid>
        </Box>
      </Dialog>
  );
};
StrippingChart.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  rowData: PropTypes.any,
  wellDetails: PropTypes.any
};
StrippingChart.defaultProps = {
  selectedJob: {}
};

export default StrippingChart;
